import { axiosInstance } from "../../App";
import { FileDTO, StudyOrderCopyPayloadDTO, StudyOrderDTO, StudyOrderDiseaseDTO } from "../../interface/interface";

export type StudyOrderValidationRulesDTO = {
  repositoryConfigured: boolean;
  validatorIdentifier?: string;
  validatorBeanName?: string;
  diagnosisMappedToCie10Required: boolean;
  allowedNomenclatorSourceIds: number[];
};

export type StudyOrderPrevalidationResponseDTO = {
  valid: boolean;
  errors: string[];
  rules?: StudyOrderValidationRulesDTO;
};

type ApiErrorPayload = {
  error?: string;
  message?: string;
  status?: number;
};

type ApiErrorWithResponse = {
  response?: {
    data?: ApiErrorPayload | string;
  };
  message?: string;
};

export function getStudyOrderErrorMessage(error: unknown, fallback: string): string {
  const err = (error ?? {}) as ApiErrorWithResponse;
  const data = err.response?.data;
  if (typeof data === "string" && data.trim()) {
    return data;
  }
  if (data && typeof data === "object" && typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (typeof err.message === "string" && err.message.trim()) {
    return err.message;
  }
  return fallback;
}

export async function getStudyOrders(
  patientId: number,
  hpsId?: number,
  size: number = 20
) {
  return axiosInstance
    .get("/api/secured/studyorders/listStudyOrders", {
      params: { patientId, hpsId, size },
    })
    .then(r => r.data as StudyOrderDTO[]);
}

export async function getRecentStudyOrdersByProfessional(
  professionalId: number,
  size: number = 20,
  offset: number = 0
) {
  return axiosInstance
    .get("/api/secured/studyorders/listRecentStudyOrdersByProfessional", {
      params: { professionalId, size, offset },
    })
    .then(r => r.data as StudyOrderDTO[]);
}

export async function countStudyOrdersByProfessional(professionalId: number) {
  return axiosInstance
    .get("/api/secured/studyorders/countStudyOrdersByProfessional", {
      params: { professionalId },
    })
    .then(r => Number(r.data ?? 0));
}

export async function getStudyOrder(id: number) {
  return axiosInstance
    .get(`/api/secured/studyorders/getStudyOrder/${id}`)
    .then(r => r.data);
}

export async function createStudyOrder(data: any) {
  return axiosInstance
    .post(`/api/secured/studyorders/createStudyOrder`, data)
    .then(r => r.data)
    .catch((error) => {
      const message = getStudyOrderErrorMessage(
        error,
        "Se produjo un error inesperado al crear la orden."
      );
      const parsed = new Error(message);
      (parsed as any).cause = error;
      throw parsed;
    });
}

export async function createWithPdf(data: any) {
  return axiosInstance
    .post(`/api/secured/studyorders/createWithPdf`, data)
    .then(r => r.data as FileDTO)
    .catch((error) => {
      const message = getStudyOrderErrorMessage(
        error,
        "Se produjo un error inesperado al crear la orden con PDF."
      );
      const parsed = new Error(message);
      (parsed as any).cause = error;
      throw parsed;
    });
}

export async function generatePdf(studyOrderId: number) {
  return axiosInstance
    .post(`/api/secured/studyorders/${studyOrderId}/generatePdf`)
    .then(r => r.data as FileDTO);
}

export async function getStudyOrderPDF(orderId: number) {
  return axiosInstance
    .get(`/api/secured/studyorders/${orderId}/getStudyOrderPdfByID`)
    .then(r => r.data as FileDTO);
}

export async function suspendStudyOrder(orderId: number) {
  return axiosInstance
    .post(`/api/secured/studyorders/${orderId}/suspend`)
    .then(r => r.data);
}

export async function getDiagnostics(orderId: number) {
  return axiosInstance
    .get(`/api/secured/studyorders/${orderId}/diagnostics`)
    .then(r => r.data);
}

export async function validateCoverage(patientId: number) {
  return axiosInstance
    .get(`/api/secured/studyorders/validateCoverage`, {
      params: { patientId },
    })
    .then(r => r.data as boolean);
}

export async function getStudyOrderValidationRules(params: {
  patientId?: number | null;
  healthPatientCoverageId?: number | null;
}) {
  return axiosInstance
    .get("/api/secured/studyorders/validationRules", {
      params: {
        patientId: params.patientId ?? undefined,
        healthPatientCoverageId: params.healthPatientCoverageId ?? undefined,
      },
    })
    .then((r) => r.data as StudyOrderValidationRulesDTO);
}

export async function prevalidateStudyOrder(data: any) {
  return axiosInstance
    .post("/api/secured/studyorders/prevalidate", data)
    .then((r) => r.data as StudyOrderPrevalidationResponseDTO)
    .catch((error) => {
      const message = getStudyOrderErrorMessage(
        error,
        "Se produjo un error inesperado al prevalidar la orden."
      );
      const parsed = new Error(message);
      (parsed as any).cause = error;
      throw parsed;
    });
}

export async function sendStudyOrder({
  fileId,
  messageType,
  patientId,
  professionalId
}: {
  fileId: number;
  messageType: number;
  patientId: number;
  professionalId: number;
}) {
  return axiosInstance.post(
    "/api/secured/studyorders/sendStudyOrderByMessageSender",
    {
      studyOrderFiles: [{ id: fileId }],
      messageTypeId: messageType,
      patientId,
      professionalId
    }
  );
}

export async function getStudyOrderCopyPayload(copyFromHpsId: number) {
  const { data } = await axiosInstance.get<StudyOrderCopyPayloadDTO>(
    "/api/secured/studyorders/copyStudyOrderPayload",
    { params: { healthPatientServiceId: copyFromHpsId } }
  );
  return data;
}

export async function searchStudyOrderDiseases(filterValue: string, firstRow: number = 0, pageSize: number = 20) {
  const { data } = await axiosInstance.get<StudyOrderDiseaseDTO[]>(
    "/api/secured/studyorders/diseases",
    {
      params: {
        filter: filterValue,
        firstRow,
        pageSize,
      },
    }
  );
  return data;
}

export async function getStudyOrderDiseaseByExactCie10(cie10: string) {
  const { data } = await axiosInstance.get<StudyOrderDiseaseDTO>(
    "/api/secured/studyorders/diseases/by-cie10",
    {
      params: {
        cie10,
      },
    }
  );
  return data;
}

export async function searchActiviaStudyOrderDiseases(params: {
  filterValue: string;
  patientId?: number | null;
  healthPatientCoverageId?: number | null;
  offSet?: number;
  pageSize?: number;
}) {
  const { data } = await axiosInstance.get<StudyOrderDiseaseDTO[]>(
    "/api/secured/studyorders/diseases/activia",
    {
      params: {
        filter: params.filterValue,
        patientId: params.patientId ?? undefined,
        healthPatientCoverageId: params.healthPatientCoverageId ?? undefined,
        firstRow: params.offSet ?? 0,
        pageSize: params.pageSize ?? 20,
      },
    }
  );
  return data;
}

export function useStudyOrderService() {
  return {
    getStudyOrderErrorMessage,
    getStudyOrders,
    getRecentStudyOrdersByProfessional,
    countStudyOrdersByProfessional,
    getStudyOrder,
    createStudyOrder,
    createWithPdf,
    generatePdf,
    getStudyOrderPDF,
    suspendStudyOrder,
    getDiagnostics,
    validateCoverage,
    getStudyOrderValidationRules,
    prevalidateStudyOrder,
    sendStudyOrder,
    getStudyOrderCopyPayload,
    searchStudyOrderDiseases,
    getStudyOrderDiseaseByExactCie10,
    searchActiviaStudyOrderDiseases,
  }
}
